import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const SuggestionsContainer = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 2px solid #bae6fd;
`;

const SuggestionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => (p.$expanded ? '1rem' : '0')};
  cursor: pointer;
  user-select: none;
  gap: 0.75rem;
`;

const SuggestionsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  line-height: 1.3;
`;

const ToggleButton = styled.button`
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #047857;
  cursor: pointer;
  padding: 0.35rem 0.75rem;
  transition: background 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  transform: none;

  &:hover {
    background: #d1fae5;
  }
`;

const SuggestionsContent = styled.div`
  display: ${props => props.expanded ? 'block' : 'none'};
  animation: ${props => props.expanded ? 'slideDown 0.3s ease' : 'none'};
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CategorySection = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CategoryTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ItemsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const ItemChip = styled.button`
  background: linear-gradient(135deg, #a8e6cf 0%, #88d8a3 100%);
  border: none;
  border-radius: 1.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    background: linear-gradient(135deg, #88d8a3 0%, #a8e6cf 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const WarningBox = styled.div`
  background: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
`;

const WarningTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #92400e;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const WarningChip = styled.span`
  background: #fef3c7;
  border: 1px solid #fbbf24;
  border-radius: 1.5rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #92400e;
  font-weight: 500;
`;

const QuickSearch = styled.div`
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #bae6fd;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const SearchResults = styled.div`
  margin-top: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SUGGESTION_CATEGORIES = [
  {
    key: 'entertainment',
    icon: '🎵',
    items: [
      { key: 'musicalInstruments', icon: '🎸' },
      { key: 'books', icon: '📚' },
    ],
  },
  {
    key: 'homeDecor',
    icon: '🏠',
    items: [
      { key: 'blankets', icon: '🛏️' },
      { key: 'carpets', icon: '🏺' },
      { key: 'curtains', icon: '🪟' },
      { key: 'cushions', icon: '🪑' },
      { key: 'chandeliers', icon: '💡' },
      { key: 'decorations', icon: '✨' },
      { key: 'decorativeLamps', icon: '🕯️' },
      { key: 'artificialPlants', icon: '🌿' },
      { key: 'photoFrames', icon: '🖼️' },
      { key: 'mirrors', icon: '🪞' },
      { key: 'tableCovers', icon: '🍽️' },
      { key: 'bedSheets', icon: '🛌' },
    ],
  },
  {
    key: 'fashionAccessories',
    icon: '👕',
    items: [
      { key: 'clothes', icon: '👔' },
      { key: 'shoes', icon: '👟' },
      { key: 'bags', icon: '👜' },
      { key: 'accessories', icon: '💼' },
      { key: 'backpacks', icon: '🎒' },
    ],
  },
  {
    key: 'kitchenDining',
    icon: '🍳',
    items: [
      { key: 'kitchenUtensils', icon: '🍴' },
      { key: 'kitchenMachines', icon: '🔧' },
      { key: 'waterBottles', icon: '💧' },
      { key: 'lunchBoxes', icon: '🍱' },
    ],
  },
  {
    key: 'schoolOffice',
    icon: '📚',
    items: [
      { key: 'schoolUniforms', icon: '👔' },
      { key: 'schoolSupplies', icon: '📝' },
      { key: 'pencilCases', icon: '✏️' },
      { key: 'notebooks', icon: '📓' },
      { key: 'artSupplies', icon: '🎨' },
      { key: 'deskOrganizers', icon: '🗂️' },
    ],
  },
  {
    key: 'personalCare',
    icon: '💆',
    items: [
      { key: 'blowDryers', icon: '💨' },
      { key: 'weighingScales', icon: '⚖️' },
      { key: 'bloodPressureDevices', icon: '🩺' },
    ],
  },
];

const DONT_OFFER_ITEMS = [
  { key: 'undergarments', icon: '🚫' },
  { key: 'medicineOrPills', icon: '💊' },
];

const ItemSuggestions = ({ onItemSelect, defaultExpanded = false }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const itemName = (key) => t(`listing.suggestions.items.${key}`);
  const categoryName = (key) => t(`listing.suggestions.categories.${key}`);

  // Flatten all items for search
  const allItems = SUGGESTION_CATEGORIES.flatMap((category) => category.items);

  // Filter items based on search query
  const filteredCategories = searchQuery
    ? SUGGESTION_CATEGORIES.map((category) => ({
        ...category,
        items: category.items.filter((item) =>
          itemName(item.key).toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter((category) => category.items.length > 0)
    : SUGGESTION_CATEGORIES;

  const handleItemClick = (name) => {
    if (onItemSelect) {
      onItemSelect(name);
    }
  };

  return (
    <CollapsibleSuggestionsPanel
      title={t('listing.suggestionsTitle')}
      defaultExpanded={defaultExpanded}
    >
      <QuickSearch>
              <SearchInput
                type="text"
                placeholder={t('listing.suggestionsSearch')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <SearchResults>
                  {allItems
                    .filter((item) =>
                      itemName(item.key).toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map((item, idx) => (
                      <ItemChip
                        key={idx}
                        onClick={() => handleItemClick(itemName(item.key))}
                      >
                        <span>{item.icon}</span>
                        {itemName(item.key)}
                      </ItemChip>
                    ))}
                </SearchResults>
              )}
            </QuickSearch>

            <CategoriesGrid>
              {filteredCategories.map((category) => (
                <CategorySection key={category.key}>
                  <CategoryTitle>
                    <span>{category.icon}</span>
                    {categoryName(category.key)}
                  </CategoryTitle>
                  <ItemsList>
                    {category.items.map((item, idx) => (
                      <ItemChip
                        key={idx}
                        onClick={() => handleItemClick(itemName(item.key))}
                      >
                        <span>{item.icon}</span>
                        {itemName(item.key)}
                      </ItemChip>
                    ))}
                  </ItemsList>
                </CategorySection>
              ))}
            </CategoriesGrid>

            <WarningBox>
              <WarningTitle>
                {t('listing.suggestionsDontOffer')}
              </WarningTitle>
              <WarningList>
                {DONT_OFFER_ITEMS.map((item, idx) => (
                  <WarningChip key={idx}>
                    {item.icon} {t(`listing.suggestions.dontOfferItems.${item.key}`)}
                  </WarningChip>
                ))}
              </WarningList>
            </WarningBox>
    </CollapsibleSuggestionsPanel>
  );
};

export const CollapsibleSuggestionsPanel = ({
  title,
  children,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <SuggestionsContainer>
      <SuggestionsHeader
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        $expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        aria-expanded={expanded}
      >
        <SuggestionsTitle>{title}</SuggestionsTitle>
        <ToggleButton
          expanded={expanded}
          type="button"
          aria-label={expanded ? t('listing.suggestionsMinimize') : t('listing.suggestionsExpand')}
        >
          {expanded ? `${t('listing.suggestionsMinimize')} ▲` : `${t('listing.suggestionsExpand')} ▼`}
        </ToggleButton>
      </SuggestionsHeader>

      <SuggestionsContent expanded={expanded}>
        {expanded && children}
      </SuggestionsContent>
    </SuggestionsContainer>
  );
};

export default ItemSuggestions;

